import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";

import { useRefillSession, useUpdateRefillSession, useCloseRefillSession } from "@/lib/api/refill-sessions";
import { useRefillSteps, useRecordRefillStep, useReviseRefillStep } from "@/lib/api/refill-steps";
import { useGrow } from "@/lib/api/grows";
import { useReservoir } from "@/lib/api/reservoirs";
import { useNutrients } from "@/lib/api/nutrients";
import { useLatestReading } from "@/lib/api/grow-readings";
import { useManualEvaluate, type MixStep } from "@/lib/api/ai-refill";
import {
  useRefillConversation,
  type RefillConversation,
} from "@/lib/refill/conversation";
import { newULID } from "@/lib/api/client";
import { usePreferences } from "@/lib/preferences";
import { tempToCelsius, formatTemp, tempUnitLabel, formatEc, ecUnitLabel } from "@/lib/units";
import type {
  GrowReading,
  RefillSession,
  RefillStep,
  ReservoirState,
  UpdateRefillInput,
  Vessel,
} from "@/lib/api/types";
import {
  defaultVesselForStep,
  defaultVesselForPhase,
} from "@/lib/refill/vessel";
import {
  applicableSteps,
  canAdvanceFromMixStep,
  mixStepAdvanceTarget,
  nearestApplicableStep,
  nextApplicableStep,
  prevApplicableStep,
} from "@/lib/refill/flow";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField } from "@/components/forms/field";
import { ErrorState } from "@/components/error-state";
import {
  CaptureCard,
  EMPTY_CAPTURE,
  type CaptureValues,
} from "@/components/refill/capture-card";
import { StepList } from "@/components/refill/step-list";
import { TrajectoryChart } from "@/components/refill/trajectory-chart";
import { ManualModeBanner } from "@/components/refill/manual-mode-banner";
import { SafetyViolationBanner } from "@/components/refill/safety-violation-banner";
import { AIPanel } from "@/components/refill/ai-panel";
import { latestAssistantText } from "@/components/refill/ai-conversation";
import { RecipeOverview, MixFlags } from "@/components/refill/recipe-overview";
import { WizardPendingActions } from "@/components/refill/wizard-pending-actions";

// The guided 8-step refill wizard (PROMPT §5). Each step is one search-param
// value (`?step=N`); the flow is intentionally non-linear — the recorded-step
// rail (undo / revise / void) and the AI panel stay live across every step.
//
// Per-step pen capture is one tap: CaptureCard pulls the latest live sample
// into the step's readings, unit-converted, and the wizard normalises to SI
// at the wire. Recording a mix step server-side also writes a
// NutrientCalibration row, so the user's per-nutrient signatures improve.

const STEPS = [
  { n: 1, label: "Current state" },
  { n: 2, label: "AI analysis" },
  { n: 3, label: "Prioritized actions" },
  { n: 4, label: "Proposed mix" },
  { n: 5, label: "Mixing wizard" },
  { n: 6, label: "Pre-pour read" },
  { n: 7, label: "Pour" },
  { n: 8, label: "Post-pour & close" },
] as const;

// One continuous AI conversation per session, owned by the always-mounted
// wizard page and shared by every AI surface: the analysis (step 2), the
// prioritized actions (step 3), per-dose mixing feedback (step 5), the
// close-out summary (step 8), and the free-form assistant panel at the bottom
// all append to the SAME thread. It's seeded from the server-persisted thread
// (llm_calls via useRefillConversation), so the analysis survives `?step=N`
// navigation, a back-press from an inline panel, AND a full page reload — none
// of which used to hold (each step kept its result in throwaway local state and
// each turn ran with an empty history).
const ConvContext = createContext<RefillConversation | null>(null);

function useConv(): RefillConversation | null {
  return useContext(ConvContext);
}

export function RefillWizardPage() {
  const { sessionId } = useParams({ from: "/refills/$sessionId/wizard" });
  const { step } = useSearch({ from: "/refills/$sessionId/wizard" });
  const navigate = useNavigate();

  const sess = useRefillSession(sessionId);
  const stepsQuery = useRefillSteps(sessionId);
  const grow = useGrow(sess.data?.grow_id);
  const planEval = useManualEvaluate(sessionId);

  const [revising, setRevising] = useState<RefillStep | null>(null);

  // The single continuous AI conversation for this session (see ConvContext),
  // owned by the always-mounted page and seeded from the server so it survives
  // step changes, inline-panel back-presses, and full reloads. Every AI surface
  // appends to it.
  const conv = useRefillConversation(sessionId);

  const goto = (n: number) =>
    navigate({
      to: "/refills/$sessionId/wizard",
      params: { sessionId },
      search: { step: n },
    });

  if (sess.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (sess.error) return <ErrorState error={sess.error} retry={sess.refetch} />;
  if (!sess.data) return null;
  const s = sess.data;

  const steps = [...(stepsQuery.data?.items ?? [])].sort(
    (a, b) => a.step_index - b.step_index,
  );
  const closed = !!s.closed_at || !!s.voided_at;

  // The applicable subset of the 8 underlying steps, inferred from the live
  // session + steps (recipe? mixing batch?). The rail, nav, and auto-advance
  // all move within this list so skipped steps are jumped automatically.
  const applicable = applicableSteps(s, steps);

  // If the URL landed on a non-applicable step (hand-edited, or a step that
  // just became hidden), gently redirect to the nearest applicable step.
  const snapped = nearestApplicableStep(step, applicable);
  if (snapped !== step) {
    return <NonApplicableRedirect goto={goto} target={snapped} />;
  }

  return (
    <ConvContext.Provider value={conv}>
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Refill wizard</h1>
          <p className="text-sm text-muted-foreground">
            {grow.data
              ? `${grow.data.species} — ${grow.data.system_type}`
              : `grow ${s.grow_id.slice(-6)}`}{" "}
            · {s.trigger}
            {s.recipe_id && ` · recipe v${s.recipe_version}`}
            {closed && (s.voided_at ? " · voided" : " · closed")}
          </p>
        </div>
        <Link
          to="/refills/$sessionId"
          params={{ sessionId }}
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          Session detail →
        </Link>
      </header>

      <StepProgress current={step} applicable={applicable} onGoto={goto} />

      {closed && (
        <div
          role="status"
          className="rounded-md border border-muted bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
        >
          This session is {s.voided_at ? "voided" : "closed"}. Steps are
          read-only; open the session detail for the full record.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-4">
          <StepBody
            step={step}
            session={s}
            steps={steps}
            grow={grow.data}
            planEval={planEval}
            goto={goto}
            applicable={applicable}
            disabled={closed}
            sessionId={sessionId}
          />
          <NavFooter current={step} applicable={applicable} onGoto={goto} />
        </div>

        <aside className="space-y-4">
          <section className="space-y-2">
            <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Trajectory
            </h2>
            <TrajectoryChart steps={steps} />
          </section>
          <section className="space-y-2">
            <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Recorded steps
            </h2>
            {revising && (
              <ReviseStepPanel
                step={revising}
                sessionId={sessionId}
                onClose={() => setRevising(null)}
              />
            )}
            <StepList
              sessionID={sessionId}
              steps={steps}
              onRevise={closed ? undefined : (st) => setRevising(st)}
            />
          </section>
        </aside>
      </div>

      <section className="space-y-2 border-t pt-4">
        <AIPanel conv={conv} />
      </section>
    </div>
    </ConvContext.Provider>
  );
}

// ─────────────────────────── step router ───────────────────────────

interface StepBodyProps {
  step: number;
  session: RefillSession;
  steps: RefillStep[];
  grow: ReturnType<typeof useGrow>["data"];
  planEval: ReturnType<typeof useManualEvaluate>;
  goto: (n: number) => void;
  // The applicable subset of underlying step numbers for this session; nav and
  // auto-advance move within it so skipped steps are jumped automatically.
  applicable: number[];
  disabled: boolean;
  sessionId: string;
}

function StepBody(props: StepBodyProps) {
  switch (props.step) {
    case 1:
      return <CaptureStateStep {...props} />;
    case 2:
      return (
        <InsightStep
          key="insight-analysis"
          {...props}
          showCurrentState
          title="AI analysis"
          blurb="Compare the current reading to prior refills and the grow's ideal bands; surface the drift mechanism. This starts one continuous conversation that carries through the next steps."
          cta="Run analysis"
          prompt="Read the current reservoir state and my chemistry profile. Compare it to this grow's ideal pH/EC/temp bands and recent refills, then explain the most likely drift mechanism (alkaline-buffer pull, salt accumulation, or exudate-driven acidic crash). Keep it to a few sentences."
        />
      );
    case 3:
      return <PrioritizedActionsStep key="insight-prioritized" {...props} />;
    case 4:
      return <ProposedMixStep {...props} />;
    case 5:
      return <MixingStep {...props} />;
    case 6:
      return <PrePourStep {...props} />;
    case 7:
      return <PourStep {...props} />;
    case 8:
      return <PostPourStep {...props} />;
    default:
      return null;
  }
}

// ─────────────────────────── step 1: current state ───────────────────────────

function CaptureStateStep({ session, grow, goto, applicable, step, disabled, sessionId }: StepBodyProps) {
  const { temperatureUnit, ecUnit } = usePreferences();
  const reservoir = useReservoir(grow?.reservoir_id);
  const latest = useLatestReading(grow?.id);
  const update = useUpdateRefillSession(sessionId);
  // step 1 baseline is the growing reservoir; the snapshot is tagged so it's
  // self-describing (pre_state → reservoir).
  const [vessel, setVessel] = useState<Vessel>(() => defaultVesselForStep(1));
  const [vals, setVals] = useState<CaptureValues>(() =>
    stateToCapture(session.pre_state, temperatureUnit),
  );
  const next = nextApplicableStep(step, applicable);

  // Prefill from the latest saved grow reading — we may have just taken a
  // measurement, so re-typing it would be busywork. Guarded so it never
  // clobbers a real capture (pre_state) or anything the user has started
  // typing: one-shot, only when this session has no pre_state and the fields
  // are still empty. The grower can Clear it if the reservoir has since changed.
  const [prefilledAt, setPrefilledAt] = useState<string | null>(null);
  const prefillTriedRef = useRef(false);
  const preStateHasReadings = stateHasReadings(session.pre_state);
  useEffect(() => {
    if (prefillTriedRef.current || preStateHasReadings) return;
    if (captureIsEmpty(vals) === false) return; // user already typed
    const r = latest.data;
    if (!r) return; // still loading, or the grow has no readings yet
    prefillTriedRef.current = true;
    setVals(readingToCapture(r, temperatureUnit));
    setPrefilledAt(r.captured_at);
  }, [latest.data, preStateHasReadings, temperatureUnit, vals]);

  function save() {
    update.mutate(
      mergeUpdate(session, {
        pre_state: captureToState(vals, temperatureUnit, vessel),
      }),
      { onSuccess: () => goto(next) },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>1 · Capture current state</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Dip the pen and tap Capture — or type the reading. This is the
          reservoir's pre-refill baseline.
        </p>
        {grow && <IdealBands grow={grow} tempUnit={temperatureUnit} ecUnit={ecUnit} />}
        {prefilledAt && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <span>
              Pulled from your last saved reading ·{" "}
              {formatStamp(prefilledAt)} — edit if the reservoir has changed.
            </span>
            <button
              type="button"
              onClick={() => {
                setVals(EMPTY_CAPTURE);
                setPrefilledAt(null);
              }}
              className="font-medium text-foreground underline-offset-4 hover:underline"
            >
              Clear
            </button>
          </div>
        )}
        <CaptureCard
          values={vals}
          onChange={setVals}
          withLevel
          vessel={vessel}
          onVesselChange={setVessel}
          reservoirFactors={{
            galPerInch: reservoir.data?.gal_per_inch,
            cmPerGal: reservoir.data?.cm_per_gal,
          }}
        />
        {update.error && (
          <ErrorState error={update.error} retry={() => update.reset()} />
        )}
        <div className="flex items-center gap-2">
          <Button type="button" disabled={disabled || update.isPending} onClick={save}>
            {update.isPending ? "Saving…" : "Save & continue"}
          </Button>
          <Button type="button" variant="ghost" onClick={() => goto(next)}>
            Skip
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────── steps 2/3: AI insight ───────────────────────────

// InsightStep runs one prompt against the SHARED session conversation (see
// ConvContext) and shows the assistant's latest reply. The thread is one
// continuous conversation — step 2's analysis, step 3's prioritized actions,
// and anything typed in the assistant panel all build on each other and persist
// across navigation/reload — so the result here is never lost and step 3 sees
// step 2's reasoning.
function InsightStep({
  title,
  blurb,
  cta,
  prompt,
  session,
  showCurrentState,
  goto,
  applicable,
  step,
}: StepBodyProps & {
  title: string;
  blurb: string;
  cta: string;
  prompt: string;
  showCurrentState?: boolean;
}) {
  const conv = useConv();
  const running = conv?.isRunning ?? false;
  const manualMode = conv?.manualMode ?? null;
  const error = conv?.error ?? null;
  const latest = conv ? latestAssistantText(conv.history) : null;
  const started = (conv?.history.length ?? 0) > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {STEPS.find((x) => x.label === title)?.n} · {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{blurb}</p>
        {showCurrentState && (
          <CapturedStateReadout
            state={session.pre_state}
            onGoToCapture={() => goto(nearestApplicableStep(1, applicable))}
            disabled={step === 1}
          />
        )}
        <Button
          type="button"
          onClick={() => conv?.run(prompt)}
          disabled={running}
        >
          {running ? "Thinking…" : started ? `Re-run · ${cta}` : cta}
        </Button>

        {manualMode ? (
          <>
            <ManualModeBanner reason={manualMode} />
            {conv?.manualData && (
              <SafetyViolationBanner
                violations={conv.manualData.safety_violations}
              />
            )}
          </>
        ) : (
          latest && (
            <div className="whitespace-pre-wrap rounded-md border bg-accent/30 px-4 py-3 text-sm">
              {latest}
            </div>
          )
        )}
        {error && !manualMode && (
          <p className="text-xs text-destructive">{error}</p>
        )}
        <p className="text-xs text-muted-foreground">
          This is one continuous conversation — continue it in the AI assistant
          panel below.
        </p>
      </CardContent>
    </Card>
  );
}

// PrioritizedActionsStep is step 3: the AI ranks the actions for this refill
// (appending to the shared thread) AND the grower works their pending actions
// right here — no navigating out to /pending-actions (which used to unmount the
// wizard and drop the analysis). Acting on an action, or asking the assistant
// to "Work with AI", updates the pending-action list in place via the same
// conversation.
function PrioritizedActionsStep(props: StepBodyProps) {
  const conv = useConv();
  return (
    <div className="space-y-4">
      <InsightStep
        {...props}
        showCurrentState
        title="Prioritized actions"
        blurb="Ranked most → least urgent, each with a one-line rationale, building on the analysis above."
        cta="Get prioritized actions"
        prompt="Based on our conversation so far, the current reservoir state, and my chemistry profile, list the actions I should take for this refill, ranked most urgent to least urgent. Give each a one-line rationale. Create or update my pending actions for this grow to match."
      />
      {conv && (
        <WizardPendingActions
          growID={props.session.grow_id}
          conv={conv}
          disabled={props.disabled}
        />
      )}
    </div>
  );
}

// Compact, read-only echo of the step-1 capture (session.pre_state) so the AI
// steps show the reading they're analyzing instead of looking empty — the
// capture is persisted on the session but was never surfaced here before.
function CapturedStateReadout({
  state,
  onGoToCapture,
  disabled,
}: {
  state?: ReservoirState;
  onGoToCapture: () => void;
  disabled?: boolean;
}) {
  const { temperatureUnit, ecUnit } = usePreferences();

  if (
    !state ||
    (state.ph === undefined &&
      state.ec === undefined &&
      state.temp_c === undefined &&
      state.level_gal === undefined)
  ) {
    return (
      <div className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
        No current state captured yet.{" "}
        {!disabled && (
          <button
            type="button"
            onClick={onGoToCapture}
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Capture it first →
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          Current state
        </span>
        {!disabled && (
          <button
            type="button"
            onClick={onGoToCapture}
            className="text-xs text-muted-foreground underline-offset-4 hover:underline"
          >
            Edit →
          </button>
        )}
      </div>
      <dl className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-sm tabular-nums">
        <Reading label="pH" value={state.ph?.toFixed(2) ?? "—"} />
        <Reading label={ecUnitLabel(ecUnit)} value={formatEc(state.ec, ecUnit)} />
        <Reading
          label={`Temp ${tempUnitLabel(temperatureUnit)}`}
          value={formatTemp(state.temp_c, temperatureUnit)}
        />
        {state.level_gal !== undefined && (
          <Reading label="Level" value={`${state.level_gal} gal`} />
        )}
      </dl>
    </div>
  );
}

function Reading({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

// ─────────────────────────── step 4: proposed mix ───────────────────────────

function ProposedMixStep({ session, planEval, goto, applicable, step, sessionId }: StepBodyProps) {
  const { ecUnit } = usePreferences();
  const update = useUpdateRefillSession(sessionId);
  const plan = planEval.data?.plan ?? [];
  const next = nextApplicableStep(step, applicable);
  // batch_size_gal sizes the transient mixing batch; it feeds the AI's
  // predict_blend / calibration math. Saved to the session on blur so it
  // persists across steps (mergeUpdate threads it through the PATCH).
  const [batchGal, setBatchGal] = useState(
    session.batch_size_gal === undefined ? "" : String(session.batch_size_gal),
  );

  function saveBatchGal() {
    const next = parseNum(batchGal);
    if (next === session.batch_size_gal) return;
    update.mutate(mergeUpdate(session, { batch_size_gal: next }));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>4 · Proposed mix</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          The engine orders the pinned recipe by its own mixing ranks and does
          the volume math. Review, then start mixing — you confirm or edit each
          dose as you go.
        </p>
        <FormField
          label="batch size (gal)"
          help="Volume of the mixing batch — sizes the per-nutrient dose math."
          className="max-w-[16rem]"
        >
          <Input
            type="number"
            step="any"
            value={batchGal}
            onChange={(e) => setBatchGal(e.target.value)}
            onBlur={saveBatchGal}
          />
        </FormField>
        {update.error && (
          <ErrorState error={update.error} retry={() => update.reset()} />
        )}
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-muted-foreground">
            decision: <span className="font-medium">{session.decision_type ?? "—"}</span>
          </span>
          {session.pre_state?.level_gal !== undefined && (
            <span className="text-muted-foreground">
              level:{" "}
              <span className="font-medium tabular-nums">
                {session.pre_state.level_gal} gal
              </span>
            </span>
          )}
        </div>

        <Button
          type="button"
          onClick={() => planEval.mutate()}
          disabled={planEval.isPending}
        >
          {planEval.isPending
            ? "Computing…"
            : plan.length
              ? "Recompute mix"
              : "Compute proposed mix"}
        </Button>

        {planEval.error && (
          <ErrorState error={planEval.error} retry={() => planEval.reset()} />
        )}

        {plan.length > 0 ? (
          <RecipeOverview plan={plan} />
        ) : (
          planEval.data && (
            <p className="text-sm text-muted-foreground">
              No recipe-driven mix steps — you can still record mix steps
              manually in the next step (pick a nutrient + dose).
            </p>
          )
        )}

        {planEval.data && (
          <p className="text-xs text-muted-foreground">
            predicted final — pH{" "}
            {planEval.data.trajectory.predicted_final_ph.toFixed(2)}, EC{" "}
            {formatEc(planEval.data.trajectory.predicted_final_ec, ecUnit)}{" "}
            {ecUnitLabel(ecUnit)}
          </p>
        )}

        <Button type="button" variant="default" onClick={() => goto(next)}>
          Start mixing →
        </Button>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────── step 5: mixing wizard ───────────────────────────

function MixingStep({
  session,
  steps,
  grow,
  planEval,
  goto,
  applicable,
  step,
  disabled,
  sessionId,
}: StepBodyProps) {
  const { ecUnit } = usePreferences();
  const plan = planEval.data?.plan ?? [];
  const activeMix = steps.filter(
    (s) => s.phase === "mix" && !s.voided_at && !s.superseded_by_id,
  );
  const recordedMixCount = activeMix.length;
  const running = runningState(steps, session.pre_state);
  const nextIndex = nextStepIndex(steps);

  const currentMix: MixStep | undefined = plan[recordedMixCount];
  const planExhausted = plan.length > 0 && !currentMix;

  // The dose card's vessel toggle is the single switch that adapts the flow:
  // 🌱 reservoir → "Add to reservoir" (direct dose, needs a volume); 🪣 mixing
  // → "Mixing wizard" (separate batch, makes pre-pour/pour appear). Lifting it
  // here lets us reframe the card title/copy and recompute applicable steps
  // live as the user flips it.
  //
  // The default is inferred from the session shape so the common case needs no
  // toggle flip: a pinned recipe is mixed in a separate batch (🪣); with no
  // recipe the typical fill is a direct-to-reservoir dose (🌱). This keeps the
  // card framing consistent with the step rail's inferred-flow hint.
  //
  // NOTE: the recipe-less default of "reservoir" is intentional (no-recipe doses
  // go direct-to-reservoir). The default is RE-SYNCED below when recipe_id
  // transitions (e.g. a recipe gets pinned while the user is on this step and
  // the query refetches), unless the user has manually flipped the toggle.
  const defaultVessel: Vessel = session.recipe_id ? "mixing_batch" : "reservoir";
  const [vessel, setVessel] = useState<Vessel>(() => defaultVessel);
  // True once the user manually flips the toggle — guards the resync below so we
  // don't fight a deliberate override.
  const userTouchedVessel = useRef(false);
  const handleVesselChange = (v: Vessel) => {
    userTouchedVessel.current = true;
    setVessel(v);
  };
  // Resync the inferred default when recipe_id transitions (the useState
  // initializer only runs once). Keyed on recipe_id so a recipe pinned after
  // mount flips the dose card to the mixing batch + surfaces the pour steps —
  // but only while the user hasn't manually overridden the toggle.
  useEffect(() => {
    if (userTouchedVessel.current) return;
    setVessel(defaultVessel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.recipe_id]);

  // "Done dosing →" advances against the session's REAL applicable list, so it
  // can never land on a step the page would then snap past. When the toggle is
  // in the mixing batch but no batch is persisted yet, advancing is gated (see
  // canAdvanceFromMixStep) — the button is disabled with a hint until a batch
  // size is set, so pre-pour/pour are never silently skipped.
  const advanceTarget = mixStepAdvanceTarget(step, session, steps);
  const canAdvance = canAdvanceFromMixStep(
    vessel === "mixing_batch",
    session,
    steps,
  );
  const advanceHint =
    vessel === "mixing_batch" && !canAdvance
      ? "Set a batch size to continue (so the pour steps aren't skipped)."
      : undefined;

  const isReservoir = vessel === "reservoir";
  const title = isReservoir ? "5 · Add to reservoir" : "5 · Mixing wizard";
  const blurb = isReservoir
    ? "Dose directly into the growing reservoir, then capture pH/EC/temp. Each recorded dose also trains your per-nutrient signature."
    : "Dose the nutrient, then capture pH/EC/temp after mixing. Each recorded mix step also trains your per-nutrient signature.";

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{blurb}</p>

        {plan.length > 0 && (
          <div className="space-y-2 rounded-md border bg-muted/20 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Recommended mix
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {recordedMixCount} of {plan.length} dosed
              </span>
            </div>
            <RecipeOverview plan={plan} dosedCount={recordedMixCount} />
            {planEval.data && (
              <p className="text-xs text-muted-foreground">
                predicted final — pH{" "}
                {planEval.data.trajectory.predicted_final_ph.toFixed(2)}, EC{" "}
                {formatEc(planEval.data.trajectory.predicted_final_ec, ecUnit)}{" "}
                {ecUnitLabel(ecUnit)}
              </p>
            )}
          </div>
        )}

        {currentMix ? (
          <MixStepCard
            key={recordedMixCount}
            mix={currentMix}
            stepIndex={nextIndex}
            running={running}
            session={session}
            grow={grow}
            vessel={vessel}
            onVesselChange={handleVesselChange}
            sessionId={sessionId}
            disabled={disabled}
            canAdvance={canAdvance}
            advanceHint={advanceHint}
            onDone={() => goto(advanceTarget)}
          />
        ) : planExhausted ? (
          <div className="space-y-3">
            <p className="text-sm">All recipe steps dosed.</p>
            <Button
              type="button"
              disabled={!canAdvance}
              onClick={() => goto(advanceTarget)}
            >
              Done dosing →
            </Button>
            {advanceHint && (
              <p className="text-xs text-muted-foreground">{advanceHint}</p>
            )}
          </div>
        ) : (
          <ManualMixStep
            key={recordedMixCount}
            stepIndex={nextIndex}
            running={running}
            session={session}
            grow={grow}
            vessel={vessel}
            onVesselChange={handleVesselChange}
            sessionId={sessionId}
            disabled={disabled}
            canAdvance={canAdvance}
            advanceHint={advanceHint}
            onDone={() => goto(advanceTarget)}
          />
        )}

        <MixFeedback
          running={running}
          plan={plan}
          dosedCount={recordedMixCount}
          currentMix={currentMix}
          ecUnit={ecUnit}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
}

// MixFeedback is the per-step AI coach for the mixing wizard (issue 6): after
// dosing and capturing a reading, the grower asks the assistant whether they're
// tracking the recipe's targets and whether any later doses / gate checkpoints
// should change given the live reading. It appends to the SHARED session thread
// (so it's one continuous conversation with the analysis + the bottom panel)
// and shows the latest reply inline. The model already has the recipe, running
// steps, and safety rails in its bundle; the prompt hands it the live running
// state and where we are in the recipe so its guidance is grounded.
function MixFeedback({
  running,
  plan,
  dosedCount,
  currentMix,
  ecUnit,
  disabled,
}: {
  running: RunningState;
  plan: MixStep[];
  dosedCount: number;
  currentMix: MixStep | undefined;
  ecUnit: "mS/cm" | "ppm700";
  disabled: boolean;
}) {
  const conv = useConv();
  const running_ = conv?.isRunning ?? false;
  const manualMode = conv?.manualMode ?? null;
  const latest = conv ? latestAssistantText(conv.history) : null;
  const started = (conv?.history.length ?? 0) > 0;

  function ask() {
    const ph = running.ph !== undefined ? running.ph.toFixed(2) : "?";
    const ec =
      running.ec !== undefined ? formatEc(running.ec, ecUnit) : "?";
    const temp = running.temp_c !== undefined ? running.temp_c.toFixed(1) : "?";
    const progress =
      plan.length > 0
        ? `I've dosed ${dosedCount} of ${plan.length} recipe steps${
            currentMix ? `; next up is ${currentMix.nutrient_name}` : " (recipe complete)"
          }.`
        : "I'm dosing manually (no pinned recipe).";
    conv?.run(
      `I'm mid-mix and just took a reading. Current batch state — pH ${ph}, EC ${ec} (${ecUnitLabel(
        ecUnit,
      )}), temp ${temp}°C. ${progress} Am I tracking the recipe's pH/EC targets? Evaluate any gate checkpoints against this reading and tell me whether to adjust the remaining doses (and by how much).`,
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-dashed p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          AI feedback on this dose
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={ask}
          disabled={disabled || running_}
        >
          {running_
            ? "Thinking…"
            : started
              ? "Re-check progress"
              : "Am I on track?"}
        </Button>
      </div>
      {manualMode ? (
        <ManualModeBanner reason={manualMode} />
      ) : latest ? (
        <div className="whitespace-pre-wrap rounded-md border bg-accent/30 px-3 py-2 text-sm">
          {latest}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          After recording a dose, ask the assistant to check your trajectory and
          adjust the remaining steps — it sees the recipe, your readings, and the
          gate checkpoints.
        </p>
      )}
    </div>
  );
}

// BatchSizeField PATCHes session.batch_size_gal (reuse of the old
// ProposedMixStep pattern). Shown on the dose card when the pen is in the
// mixing batch — setting it makes the pre-pour/pour steps appear.
function BatchSizeField({
  session,
  sessionId,
}: {
  session: RefillSession;
  sessionId: string;
}) {
  const update = useUpdateRefillSession(sessionId);
  const [batchGal, setBatchGal] = useState(
    session.batch_size_gal === undefined ? "" : String(session.batch_size_gal),
  );

  function saveBatchGal() {
    const next = parseNum(batchGal);
    if (next === session.batch_size_gal) return;
    update.mutate(mergeUpdate(session, { batch_size_gal: next }));
  }

  return (
    <>
      <FormField
        label="batch size (gal)"
        help="Volume of the mixing batch — sizes the per-nutrient dose math and unlocks the pour steps."
        className="max-w-[16rem]"
      >
        <Input
          type="number"
          step="any"
          value={batchGal}
          onChange={(e) => setBatchGal(e.target.value)}
          onBlur={saveBatchGal}
        />
      </FormField>
      {update.error && (
        <ErrorState error={update.error} retry={() => update.reset()} />
      )}
    </>
  );
}

// ReservoirVolumeField is a REQUIRED inline volume for direct-to-reservoir
// doses: the backend reads the reservoir level at record time to compute the
// nutrient's mL-per-gal signature. Only rendered when pre_state.level_gal is
// missing; the parent disables Record until a volume is available.
function ReservoirVolumeField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <FormField
      label="reservoir volume (gal)"
      help="Needed to learn this nutrient's strength."
      className="max-w-[16rem]"
    >
      <Input
        type="number"
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </FormField>
  );
}

function MixStepCard({
  mix,
  stepIndex,
  running,
  session,
  grow,
  vessel,
  onVesselChange,
  sessionId,
  disabled,
  canAdvance,
  advanceHint,
  onDone,
}: {
  mix: MixStep;
  stepIndex: number;
  running: RunningState;
  session: RefillSession;
  grow: ReturnType<typeof useGrow>["data"];
  vessel: Vessel;
  onVesselChange: (v: Vessel) => void;
  sessionId: string;
  disabled: boolean;
  // Whether "Done dosing →" may advance — false (with advanceHint set) when the
  // pen is in the mixing batch but no batch is persisted yet, so advancing would
  // skip the pre-pour/pour steps.
  canAdvance: boolean;
  advanceHint?: string;
  onDone: () => void;
}) {
  const { temperatureUnit } = usePreferences();
  const reservoir = useReservoir(grow?.reservoir_id);
  const update = useUpdateRefillSession(sessionId);
  const record = useRecordRefillStep(sessionId);
  const [ml, setMl] = useState(String(mix.amount_ml));
  const [vals, setVals] = useState<CaptureValues>(EMPTY_CAPTURE);
  // Local volume for the direct-dose path; only needed/used when the pen is in
  // the reservoir and the session has no level yet.
  const [volGal, setVolGal] = useState("");

  const isReservoir = vessel === "reservoir";
  const hasLevel = session.pre_state?.level_gal !== undefined;
  const needsVolume = isReservoir && !hasLevel;
  const haveVolume = hasLevel || parseNum(volGal) !== undefined;

  function recordStep() {
    const post = captureToState(vals, temperatureUnit, vessel);
    const doRecord = () =>
      record.mutate({
        id: newULID(),
        step_index: stepIndex,
        phase: "mix",
        target: vessel,
        nutrient_id: mix.nutrient_id,
        ml_dosed: parseNum(ml),
        pre_ph: running.ph,
        pre_ec: running.ec,
        pre_temp_c: running.temp_c,
        post_ph: post.ph,
        post_ec: post.ec,
        post_temp_c: post.temp_c,
      });
    // Direct dose with a freshly-entered volume: PATCH the level into pre_state
    // FIRST (merge so pH/EC/temp/vessel aren't clobbered), THEN record — the
    // backend reads the reservoir level at record time.
    if (needsVolume && parseNum(volGal) !== undefined) {
      update.mutate(
        mergeUpdate(session, {
          pre_state: {
            ...(session.pre_state ?? { captured_at: new Date().toISOString() }),
            level_gal: parseNum(volGal),
          },
        }),
        { onSuccess: doRecord },
      );
    } else {
      doRecord();
    }
  }

  return (
    <div className="space-y-3 rounded-md border border-primary/40 p-4">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">
          {mix.order_rank}. {mix.nutrient_name}
        </span>
        <span className="text-xs text-muted-foreground">
          target {mix.amount_ml.toFixed(1)} mL · {mix.ml_per_gal} mL/gal
        </span>
      </div>
      <MixFlags m={mix} />
      {mix.gate_rules.length > 0 && (
        <ul className="space-y-0.5 text-xs text-amber-700 dark:text-amber-300">
          {mix.gate_rules.map((g, i) => (
            <li key={i}>
              ⚠ gate: if {g.if} → {g.then}
            </li>
          ))}
        </ul>
      )}
      <FormField label="mL dosed">
        <Input
          type="number"
          step="any"
          value={ml}
          onChange={(e) => setMl(e.target.value)}
        />
      </FormField>
      {vessel === "mixing_batch" && (
        <BatchSizeField session={session} sessionId={sessionId} />
      )}
      {needsVolume && (
        <ReservoirVolumeField value={volGal} onChange={setVolGal} />
      )}
      <div className="text-xs text-muted-foreground">Reading after dosing:</div>
      <CaptureCard
        values={vals}
        onChange={setVals}
        vessel={vessel}
        onVesselChange={onVesselChange}
        reservoirFactors={{
          galPerInch: reservoir.data?.gal_per_inch,
          cmPerGal: reservoir.data?.cm_per_gal,
        }}
      />
      {(record.error || update.error) && (
        <ErrorState
          error={(record.error ?? update.error) as Error}
          retry={() => {
            record.reset();
            update.reset();
          }}
        />
      )}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          disabled={
            disabled || record.isPending || update.isPending || (needsVolume && !haveVolume)
          }
          onClick={recordStep}
        >
          {record.isPending || update.isPending ? "Recording…" : "Record step"}
        </Button>
        <Button
          type="button"
          variant="default"
          disabled={!canAdvance}
          onClick={onDone}
        >
          Done dosing →
        </Button>
      </div>
      {advanceHint && (
        <p className="text-xs text-muted-foreground">{advanceHint}</p>
      )}
    </div>
  );
}

function ManualMixStep({
  stepIndex,
  running,
  session,
  grow,
  vessel,
  onVesselChange,
  sessionId,
  disabled,
  canAdvance,
  advanceHint,
  onDone,
}: {
  stepIndex: number;
  running: RunningState;
  session: RefillSession;
  grow: ReturnType<typeof useGrow>["data"];
  vessel: Vessel;
  onVesselChange: (v: Vessel) => void;
  sessionId: string;
  disabled: boolean;
  // See MixStepCard: gates "Done dosing →" so it can't skip the pour steps.
  canAdvance: boolean;
  advanceHint?: string;
  onDone: () => void;
}) {
  const { temperatureUnit } = usePreferences();
  const nutrients = useNutrients();
  const reservoir = useReservoir(grow?.reservoir_id);
  const update = useUpdateRefillSession(sessionId);
  const record = useRecordRefillStep(sessionId);
  const [nutrientId, setNutrientId] = useState("");
  const [ml, setMl] = useState("");
  const [vals, setVals] = useState<CaptureValues>(EMPTY_CAPTURE);
  const [volGal, setVolGal] = useState("");

  const isReservoir = vessel === "reservoir";
  const hasLevel = session.pre_state?.level_gal !== undefined;
  const needsVolume = isReservoir && !hasLevel;
  const haveVolume = hasLevel || parseNum(volGal) !== undefined;

  function recordStep() {
    const post = captureToState(vals, temperatureUnit, vessel);
    const doRecord = () =>
      record.mutate({
        id: newULID(),
        step_index: stepIndex,
        phase: "mix",
        target: vessel,
        nutrient_id: nutrientId || undefined,
        ml_dosed: parseNum(ml),
        pre_ph: running.ph,
        pre_ec: running.ec,
        pre_temp_c: running.temp_c,
        post_ph: post.ph,
        post_ec: post.ec,
        post_temp_c: post.temp_c,
      });
    // Direct dose: PATCH the entered reservoir volume into pre_state.level_gal
    // FIRST (merge so existing pH/EC/temp/vessel survive), THEN record — order
    // matters because the backend reads the level at record time.
    if (needsVolume && parseNum(volGal) !== undefined) {
      update.mutate(
        mergeUpdate(session, {
          pre_state: {
            ...(session.pre_state ?? { captured_at: new Date().toISOString() }),
            level_gal: parseNum(volGal),
          },
        }),
        { onSuccess: doRecord },
      );
    } else {
      doRecord();
    }
  }

  return (
    <div className="space-y-3 rounded-md border p-4">
      <p className="text-xs text-muted-foreground">
        {isReservoir
          ? "Dosing directly into the reservoir — pick a nutrient and dose."
          : "No recipe pinned — record mix steps manually."}
      </p>
      <FormField label="nutrient">
        <Select value={nutrientId} onValueChange={setNutrientId}>
          <SelectTrigger>
            <SelectValue placeholder="Pick a nutrient…" />
          </SelectTrigger>
          <SelectContent>
            {nutrients.data?.items.map((n) => (
              <SelectItem key={n.id} value={n.id}>
                {n.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
      <FormField label="mL dosed">
        <Input
          type="number"
          step="any"
          value={ml}
          onChange={(e) => setMl(e.target.value)}
        />
      </FormField>
      {vessel === "mixing_batch" && (
        <BatchSizeField session={session} sessionId={sessionId} />
      )}
      {needsVolume && (
        <ReservoirVolumeField value={volGal} onChange={setVolGal} />
      )}
      <CaptureCard
        values={vals}
        onChange={setVals}
        vessel={vessel}
        onVesselChange={onVesselChange}
        reservoirFactors={{
          galPerInch: reservoir.data?.gal_per_inch,
          cmPerGal: reservoir.data?.cm_per_gal,
        }}
      />
      {(record.error || update.error) && (
        <ErrorState
          error={(record.error ?? update.error) as Error}
          retry={() => {
            record.reset();
            update.reset();
          }}
        />
      )}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          disabled={
            disabled ||
            record.isPending ||
            update.isPending ||
            !nutrientId ||
            (needsVolume && !haveVolume)
          }
          onClick={recordStep}
        >
          {record.isPending || update.isPending
            ? "Recording…"
            : "Record mix step"}
        </Button>
        <Button
          type="button"
          variant="default"
          disabled={!canAdvance}
          onClick={onDone}
        >
          Done dosing →
        </Button>
      </div>
      {advanceHint && (
        <p className="text-xs text-muted-foreground">{advanceHint}</p>
      )}
    </div>
  );
}

// ─────────────────────────── step 6: pre-pour ───────────────────────────

function PrePourStep({ session, steps, goto, applicable, step, disabled, sessionId }: StepBodyProps) {
  const { temperatureUnit } = usePreferences();
  const update = useUpdateRefillSession(sessionId);
  const record = useRecordRefillStep(sessionId);
  const running = runningState(steps, session.pre_state);
  // step 6 reads the mixed batch right before pouring (pre_pour_state →
  // mixing_batch); the user can override if they re-dipped the reservoir.
  const [vessel, setVessel] = useState<Vessel>(() =>
    defaultVesselForStep(6),
  );
  const [vals, setVals] = useState<CaptureValues>(() =>
    stateToCapture(session.pre_pour_state, temperatureUnit),
  );
  const nextIndex = nextStepIndex(steps);
  const next = nextApplicableStep(step, applicable);

  function save() {
    const state = captureToState(vals, temperatureUnit, vessel);
    update.mutate(
      mergeUpdate(session, { pre_pour_state: state }),
      {
        onSuccess: () =>
          record.mutate(
            {
              id: newULID(),
              step_index: nextIndex,
              phase: "pre_pour",
              target: vessel,
              pre_ph: running.ph,
              pre_ec: running.ec,
              pre_temp_c: running.temp_c,
              post_ph: state.ph,
              post_ec: state.ec,
              post_temp_c: state.temp_c,
            },
            { onSuccess: () => goto(next) },
          ),
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>6 · Pre-pour baseline read</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Capture the mixed batch right before pouring it into the reservoir.
        </p>
        <CaptureCard
          values={vals}
          onChange={setVals}
          withLevel
          vessel={vessel}
          onVesselChange={setVessel}
        />
        {(update.error || record.error) && (
          <ErrorState
            error={(update.error ?? record.error) as Error}
            retry={() => {
              update.reset();
              record.reset();
            }}
          />
        )}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            disabled={disabled || update.isPending || record.isPending}
            onClick={save}
          >
            {update.isPending || record.isPending ? "Saving…" : "Save & continue"}
          </Button>
          <Button type="button" variant="ghost" onClick={() => goto(next)}>
            Skip
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────── step 7: pour ───────────────────────────

function PourStep({ session, steps, goto, applicable, step, disabled, sessionId }: StepBodyProps) {
  const { temperatureUnit } = usePreferences();
  const record = useRecordRefillStep(sessionId);
  const running = runningState(steps, session.pre_state);
  // pour reads are back in the growing reservoir.
  const [vessel, setVessel] = useState<Vessel>(() => defaultVesselForStep(7));
  const [vals, setVals] = useState<CaptureValues>(EMPTY_CAPTURE);
  const pourCount = steps.filter(
    (s) => s.phase === "pour" && !s.voided_at && !s.superseded_by_id,
  ).length;
  const nextIndex = nextStepIndex(steps);
  const next = nextApplicableStep(step, applicable);

  function recordPour() {
    const post = captureToState(vals, temperatureUnit, vessel);
    record.mutate(
      {
        id: newULID(),
        step_index: nextIndex,
        phase: "pour",
        target: vessel,
        pre_ph: running.ph,
        pre_ec: running.ec,
        pre_temp_c: running.temp_c,
        post_ph: post.ph,
        post_ec: post.ec,
        post_temp_c: post.temp_c,
      },
      { onSuccess: () => setVals(EMPTY_CAPTURE) },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>7 · Pour</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Pour in stages to avoid overshoot. Capture an intermediate read after
          each stage — record as many pour readings as you need.
        </p>
        {pourCount > 0 && (
          <p className="text-xs text-muted-foreground">
            {pourCount} pour reading{pourCount === 1 ? "" : "s"} recorded
          </p>
        )}
        <CaptureCard
          values={vals}
          onChange={setVals}
          vessel={vessel}
          onVesselChange={setVessel}
        />
        {record.error && (
          <ErrorState error={record.error} retry={() => record.reset()} />
        )}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={disabled || record.isPending}
            onClick={recordPour}
          >
            {record.isPending ? "Recording…" : "Record pour reading"}
          </Button>
          <Button type="button" onClick={() => goto(next)}>
            Done pouring →
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────── step 8: post-pour & close ───────────────────────────

function PostPourStep({ session, grow, steps, goto, applicable, step, disabled, sessionId }: StepBodyProps) {
  const { temperatureUnit } = usePreferences();
  const reservoir = useReservoir(grow?.reservoir_id);
  const update = useUpdateRefillSession(sessionId);
  const record = useRecordRefillStep(sessionId);
  const close = useCloseRefillSession(sessionId);
  const running = runningState(steps, session.pre_state);
  // step 8 is always the last applicable step; nextApplicableStep clamps here.
  const next = nextApplicableStep(step, applicable);
  // final read is the growing reservoir (post_pour_state → reservoir).
  const [vessel, setVessel] = useState<Vessel>(() => defaultVesselForStep(8));
  const [vals, setVals] = useState<CaptureValues>(() =>
    stateToCapture(session.post_pour_state, temperatureUnit),
  );
  const [saved, setSaved] = useState(false);
  const nextIndex = nextStepIndex(steps);

  function saveFinal() {
    const state = captureToState(vals, temperatureUnit, vessel);
    update.mutate(
      mergeUpdate(session, { post_pour_state: state }),
      {
        onSuccess: () =>
          record.mutate(
            {
              id: newULID(),
              step_index: nextIndex,
              phase: "post_pour",
              target: vessel,
              pre_ph: running.ph,
              pre_ec: running.ec,
              pre_temp_c: running.temp_c,
              post_ph: state.ph,
              post_ec: state.ec,
              post_temp_c: state.temp_c,
            },
            { onSuccess: () => setSaved(true) },
          ),
      },
    );
  }

  const closedAlready = !!session.closed_at || !!session.voided_at;

  return (
    <Card>
      <CardHeader>
        <CardTitle>8 · Post-pour final state & insights</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Capture the final reservoir state. Closing the session exports the
          verbatim markdown log and refreshes pending actions.
        </p>
        <CaptureCard
          values={vals}
          onChange={setVals}
          withLevel
          vessel={vessel}
          onVesselChange={setVessel}
          reservoirFactors={{
            galPerInch: reservoir.data?.gal_per_inch,
            cmPerGal: reservoir.data?.cm_per_gal,
          }}
        />
        {(update.error || record.error) && (
          <ErrorState
            error={(update.error ?? record.error) as Error}
            retry={() => {
              update.reset();
              record.reset();
            }}
          />
        )}
        <Button
          type="button"
          variant="outline"
          disabled={disabled || update.isPending || record.isPending}
          onClick={saveFinal}
        >
          {update.isPending || record.isPending
            ? "Saving…"
            : saved
              ? "Final state saved ✓"
              : "Save final state"}
        </Button>

        <InsightInline
          label="Insights & update pending actions"
          prompt="The refill is done. Summarize what happened, the key insights for next time, and update my pending actions for this grow accordingly."
        />

        {close.error && (
          <ErrorState error={close.error} retry={() => close.reset()} />
        )}
        {!closedAlready ? (
          <Button
            type="button"
            disabled={close.isPending}
            onClick={() =>
              close.mutate(undefined, {
                onSuccess: () => goto(next),
              })
            }
          >
            {close.isPending ? "Closing…" : "Close session"}
          </Button>
        ) : (
          <Link
            to="/refills/$sessionId"
            params={{ sessionId }}
            className="inline-block text-sm font-medium underline-offset-4 hover:underline"
          >
            Session closed — view detail →
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

// Compact one-shot AI action used inline on the final step. Appends to the
// SHARED session conversation (see ConvContext) so its result joins the same
// continuous thread the wizard and assistant panel use, and survives
// navigation/reload via the server-persisted history.
function InsightInline({ label, prompt }: { label: string; prompt: string }) {
  const conv = useConv();
  const running = conv?.isRunning ?? false;
  const manualMode = conv?.manualMode ?? null;
  const latest = conv ? latestAssistantText(conv.history) : null;
  const started = (conv?.history.length ?? 0) > 0;

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => conv?.run(prompt)}
        disabled={running}
      >
        {running ? "Thinking…" : started ? "Re-run insights" : label}
      </Button>
      {manualMode ? (
        <ManualModeBanner reason={manualMode} />
      ) : (
        latest && (
          <div className="whitespace-pre-wrap rounded-md border bg-accent/30 px-3 py-2 text-sm">
            {latest}
          </div>
        )
      )}
    </div>
  );
}

// ─────────────────────────── revise panel ───────────────────────────

function ReviseStepPanel({
  step,
  sessionId,
  onClose,
}: {
  step: RefillStep;
  sessionId: string;
  onClose: () => void;
}) {
  const { temperatureUnit } = usePreferences();
  const revise = useReviseRefillStep(step.id, sessionId);
  const [ml, setMl] = useState(step.ml_dosed === undefined ? "" : String(step.ml_dosed));
  const [reason, setReason] = useState("");
  // Seed from the original step's vessel so revising preserves the tag (older
  // steps recorded before this field shipped fall back to the phase default).
  const [vessel, setVessel] = useState<Vessel>(
    () => step.target ?? defaultVesselForPhase(step.phase),
  );
  const [vals, setVals] = useState<CaptureValues>(() => ({
    ph: step.post_ph === undefined ? "" : String(step.post_ph),
    ec: step.post_ec === undefined ? "" : String(step.post_ec),
    temp:
      step.post_temp_c === undefined
        ? ""
        : tempToCelsiusInverse(step.post_temp_c, temperatureUnit),
    level: "",
  }));

  function submit() {
    const post = captureToState(vals, temperatureUnit, vessel);
    revise.mutate(
      {
        new_id: newULID(),
        step_index: step.step_index,
        phase: step.phase,
        // Preserve (or override) the original step's vessel tag.
        target: vessel,
        target_ref_id: step.target_ref_id,
        nutrient_id: step.nutrient_id,
        ml_dosed: parseNum(ml),
        pre_ph: step.pre_ph,
        pre_ec: step.pre_ec,
        pre_temp_c: step.pre_temp_c,
        post_ph: post.ph,
        post_ec: post.ec,
        post_temp_c: post.temp_c,
        reason,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-primary/40 bg-accent/20 p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">
          Revise #{step.step_index} {step.phase}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </div>
      {step.ml_dosed !== undefined && (
        <FormField label="mL dosed">
          <Input
            type="number"
            step="any"
            value={ml}
            onChange={(e) => setMl(e.target.value)}
          />
        </FormField>
      )}
      <CaptureCard
        values={vals}
        onChange={setVals}
        vessel={vessel}
        onVesselChange={setVessel}
      />
      <FormField label="reason (required)">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} />
      </FormField>
      {revise.error && (
        <p className="text-xs text-destructive">{(revise.error as Error).message}</p>
      )}
      <Button
        type="button"
        size="sm"
        disabled={!reason || revise.isPending}
        onClick={submit}
      >
        {revise.isPending ? "Saving…" : "Save revision"}
      </Button>
    </div>
  );
}

// ─────────────────────────── shared bits ───────────────────────────

function IdealBands({
  grow,
  tempUnit,
  ecUnit,
}: {
  grow: NonNullable<ReturnType<typeof useGrow>["data"]>;
  tempUnit: "C" | "F";
  ecUnit: "mS/cm" | "ppm700";
}) {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
      <span>
        pH ideal{" "}
        <span className="font-medium tabular-nums">
          {grow.ph_ideal ?? "—"}
        </span>{" "}
        ({grow.ph_min ?? "—"}–{grow.ph_max ?? "—"})
      </span>
      <span>
        EC ideal{" "}
        <span className="font-medium tabular-nums">
          {grow.ec_ideal !== undefined ? formatEc(grow.ec_ideal, ecUnit) : "—"}
        </span>{" "}
        {ecUnitLabel(ecUnit)}
      </span>
      <span>
        temp ideal{" "}
        <span className="font-medium tabular-nums">
          {grow.temp_ideal_c !== undefined
            ? formatTemp(grow.temp_ideal_c, tempUnit, 1)
            : "—"}
        </span>{" "}
        {tempUnitLabel(tempUnit)}
      </span>
    </div>
  );
}

function StepProgress({
  current,
  applicable,
  onGoto,
}: {
  current: number;
  applicable: number[];
  onGoto: (n: number) => void;
}) {
  // Only the applicable steps appear; they're renumbered by POSITION (1..N) for
  // display but navigate to the real underlying step number. Some steps being
  // hidden is the inferred-flow's whole point, so we surface a glanceable hint.
  const hidden = STEPS.filter((st) => !applicable.includes(st.n));
  return (
    <div className="space-y-1.5">
      <ol className="flex flex-wrap gap-1.5">
        {applicable.map((n, i) => {
          const label = STEPS.find((x) => x.n === n)?.label ?? "";
          const active = n === current;
          return (
            <li key={n}>
              <button
                type="button"
                onClick={() => onGoto(n)}
                className={
                  "rounded-full px-3 py-1 text-xs transition-colors " +
                  (active
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-accent")
                }
              >
                {i + 1}. {label}
              </button>
            </li>
          );
        })}
      </ol>
      {hidden.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Direct-to-reservoir fill — mixing &amp; pour steps hidden. Switch the
          pen to 🪣 Mixing (or set a batch size) on the dose step to add a
          mixing batch.
        </p>
      )}
    </div>
  );
}

function NavFooter({
  current,
  applicable,
  onGoto,
}: {
  current: number;
  applicable: number[];
  onGoto: (n: number) => void;
}) {
  // Back/Next move within the applicable list, so skipped steps are jumped.
  const prev = prevApplicableStep(current, applicable);
  const next = nextApplicableStep(current, applicable);
  return (
    <div className="flex items-center justify-between">
      <Button
        type="button"
        variant="ghost"
        disabled={prev === current}
        onClick={() => onGoto(prev)}
      >
        ← Back
      </Button>
      <Button
        type="button"
        variant="ghost"
        disabled={next === current}
        onClick={() => onGoto(next)}
      >
        Next →
      </Button>
    </div>
  );
}

// NonApplicableRedirect renders when the URL's ?step landed on a step that's
// not applicable to this session (hand-edited, or a step that just became
// hidden). It nudges the user to the nearest applicable step on mount.
function NonApplicableRedirect({
  goto,
  target,
}: {
  goto: (n: number) => void;
  target: number;
}) {
  useEffect(() => {
    goto(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return (
    <p className="text-sm text-muted-foreground">
      This step doesn't apply to this fill — taking you to the nearest step…
    </p>
  );
}

// ─────────────────────────── helpers ───────────────────────────

interface RunningState {
  ph?: number;
  ec?: number;
  temp_c?: number;
}

// runningState is the reservoir/batch state going *into* the next step: the
// most recent active step's post readings, falling back to the session's
// pre-state. Used to fill a step's pre_* readings so calibration deltas are
// computed against the right baseline.
function runningState(
  steps: RefillStep[],
  preState: ReservoirState | undefined,
): RunningState {
  const active = steps.filter((s) => !s.voided_at && !s.superseded_by_id);
  for (let i = active.length - 1; i >= 0; i--) {
    const st = active[i];
    if (st && (st.post_ph !== undefined || st.post_ec !== undefined)) {
      return { ph: st.post_ph, ec: st.post_ec, temp_c: st.post_temp_c };
    }
  }
  return { ph: preState?.ph, ec: preState?.ec, temp_c: preState?.temp_c };
}

function nextStepIndex(steps: RefillStep[]): number {
  if (steps.length === 0) return 0;
  return Math.max(...steps.map((s) => s.step_index)) + 1;
}

// mergeUpdate echoes every existing session field into the update payload,
// overriding only what `patch` changes. The PATCH endpoint is a full replace
// (it SETs every column to the request value), so omitting a field would null
// it — e.g. saving pre_pour_state alone would otherwise wipe the recipe pin.
function mergeUpdate(
  session: RefillSession,
  patch: Partial<UpdateRefillInput>,
): UpdateRefillInput {
  return {
    expected_updated_at: session.updated_at,
    decision_type: session.decision_type,
    recipe_id: session.recipe_id,
    recipe_version: session.recipe_version,
    pre_state: session.pre_state,
    pre_pour_state: session.pre_pour_state,
    post_pour_state: session.post_pour_state,
    predicted_state: session.predicted_state,
    batch_size_gal: session.batch_size_gal,
    notes: session.notes,
    ...patch,
  };
}

function parseNum(v: string): number | undefined {
  if (v.trim() === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

// captureToState normalises display-unit capture fields to the SI wire shape.
// `vessel` tags the snapshot with where the pen was read so the stored state
// is self-describing (pre_state→reservoir, pre_pour_state→mixing_batch, …).
function captureToState(
  v: CaptureValues,
  tempUnit: "C" | "F",
  vessel?: Vessel,
): ReservoirState {
  const t = parseNum(v.temp);
  return {
    ph: parseNum(v.ph),
    ec: parseNum(v.ec),
    temp_c: t === undefined ? undefined : tempToCelsius(t, tempUnit),
    level_gal: parseNum(v.level),
    vessel,
    captured_at: new Date().toISOString(),
  };
}

// stateToCapture seeds the capture fields from an existing stored state so
// re-entering a step shows what was already captured.
function stateToCapture(
  state: ReservoirState | undefined,
  tempUnit: "C" | "F",
): CaptureValues {
  if (!state) return EMPTY_CAPTURE;
  return {
    ph: state.ph === undefined ? "" : String(state.ph),
    ec: state.ec === undefined ? "" : String(state.ec),
    temp:
      state.temp_c === undefined
        ? ""
        : tempToCelsiusInverse(state.temp_c, tempUnit),
    level: state.level_gal === undefined ? "" : String(state.level_gal),
  };
}

// Stored C → display-unit string (no leading zeros padding).
function tempToCelsiusInverse(c: number, tempUnit: "C" | "F"): string {
  const v = tempUnit === "F" ? (c * 9) / 5 + 32 : c;
  return String(Number(v.toFixed(1)));
}

// readingToCapture seeds the step-1 capture fields from a saved grow reading
// (same SI conventions as stateToCapture: EC is mS/cm verbatim, temp converts
// to the display unit). Lets us prefill the current state from "the latest
// measurement we may have just taken."
function readingToCapture(
  r: GrowReading,
  tempUnit: "C" | "F",
): CaptureValues {
  return {
    ph: r.ph === undefined ? "" : String(r.ph),
    ec: r.ec_ms_per_cm === undefined ? "" : String(r.ec_ms_per_cm),
    temp:
      r.temp_c === undefined ? "" : tempToCelsiusInverse(r.temp_c, tempUnit),
    level: r.level_gal === undefined ? "" : String(r.level_gal),
  };
}

// stateHasReadings reports whether a stored snapshot carries any actual
// reading — used to decide if step 1 was already captured (so prefill backs off).
function stateHasReadings(state: ReservoirState | undefined): boolean {
  return (
    !!state &&
    (state.ph !== undefined ||
      state.ec !== undefined ||
      state.temp_c !== undefined ||
      state.level_gal !== undefined)
  );
}

// captureIsEmpty reports whether the user hasn't entered anything yet, so a
// one-shot prefill never clobbers in-progress typing.
function captureIsEmpty(v: CaptureValues): boolean {
  return !v.ph && !v.ec && !v.temp && !v.level;
}

// formatStamp renders an ISO timestamp as a compact local string for the
// "pulled from your last reading" hint.
function formatStamp(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}
